import { FC, ReactNode, useCallback, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { classNames } from '../../utils';
import useFadeInOnScroll from '../../views/hooks/useFadeInOnScroll';
import useInfiniteScroll from '../../views/hooks/useInfiniteScroll';
import { TableSkeleton } from '../Skeleton';
import { ObjectType } from '../../types/commonTypes';

export enum CellTypeEnum {
  TH = 'th',
  TD = 'tr',
}

export type TableColumnType = TableCell & {
  cellClassName?: string;
  cellType?: CellTypeEnum;
  contentTemplate?: (col: TableColumnType, translation: TFunction) => JSX.Element;
};
export interface TableCell {
  text: string;
  dataRef: string;
  className?: string;
  /** Optional column width (e.g. '19%') used to build a <colgroup> for fixed-layout tables. */
  width?: string;
  type?: CellTypeEnum;
  isVisible?: boolean;
  isExportable?: boolean;
  isSortable?: boolean;
  exportFn?: (arg: string | number, x?: ObjectType) => string;
  cellContentTemplate?: (cell: TableCell, translation: TFunction, rowData?: TableRow['rowData']) => ReactNode;
}

export interface TableRow {
  id?: string;
  onClick?: (row: TableRow) => void;
  className: string;
  cells: TableCell[];
  rowData?: Record<string, unknown>;
}
interface TableProp {
  columns: TableColumnType[];
  onTableEvent?: (event: TableRow | TableColumnType) => void;
  rows: TableRow[];
  className?: string;
  theadClassName?: string;
  containerClassNames?: string;
  isFadeOnScrollDisable?: boolean;
  applyfadeOnScrollClassName?: boolean;
}

const Table: FC<TableProp> = ({
  columns,
  rows,
  className,
  theadClassName,
  containerClassNames,
  onTableEvent,
  isFadeOnScrollDisable = false,
  applyfadeOnScrollClassName = true,
}) => {
  const [selectedRow, setSelectedRow] = useState('');
  const { t } = useTranslation();
  const { isLoading, results, lastItemRef } = useInfiniteScroll(rows);

  const processIntersectionObserverEntry = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!isFadeOnScrollDisable) {
        entry.target.classList.toggle('visible', entry.isIntersecting);
        entry.target.classList.toggle('collapse', !entry.isIntersecting);
      }
    },
    [isFadeOnScrollDisable],
  );

  const { isItemVisible, fadeOnScrollClassName } = useFadeInOnScroll({ isLoading, processIntersectionObserverEntry });

  return (
    <div className={classNames('overflow-x-auto overflow-y-hidden relative rounded-xl', containerClassNames || '')}>
      <table
        className={`table-auto w-full text-sm text-left text-gray-700 dark:text-gray-200 ${className || ''}`.trim()}
      >
        <thead
          className={`text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 bg-gradient-to-r from-gray-50 to-gray-100/80 dark:from-slate-800 dark:to-slate-800/80 backdrop-blur-sm sticky top-0 z-10 ${
            theadClassName || ''
          }`.trim()}
        >
          <tr className="border-b-2 border-gray-200/80 dark:border-slate-600/80">
            {columns.map((col, index) => {
              const isVisible = col.isVisible !== undefined ? col.isVisible : true;
              if (!isVisible) return null;
              return (
                <th
                  key={`col-${index}`}
                  scope="col"
                  className={`${col.contentTemplate ? '' : 'py-4 px-4 whitespace-nowrap'} ${
                    col.className || ''
                  }`.trim()}
                >
                  {col.contentTemplate ? col.contentTemplate(col, t) : `${col.text}`}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100/80 dark:divide-slate-700/50">
          {results.map((row, rIndex) => {
            const rowId = row.id || `${rIndex}`;
            const isSelected = rowId === selectedRow;
            const isEven = rIndex % 2 === 0;
            let lastElemRefOption = { ref: isItemVisible };
            if (results.length === rIndex + 1) {
              lastElemRefOption = { ref: lastItemRef };
            }
            return (
              <tr
                {...lastElemRefOption}
                key={`row-${rIndex}`}
                onClick={() => {
                  if (row.onClick || onTableEvent) {
                    setSelectedRow(rowId);
                    if (row.onClick) row.onClick({ ...row });
                    else onTableEvent!({ ...row } as TableRow);
                  }
                }}
                className={`
                  ${applyfadeOnScrollClassName ? fadeOnScrollClassName : ''}
                  ${isEven ? 'bg-white dark:bg-slate-800' : 'bg-gray-50/50 dark:bg-slate-800/50'}
                  ${
                    isSelected
                      ? '!bg-orange/5 dark:!bg-teal-500/10 ring-1 ring-inset ring-orange/20 dark:ring-teal-500/20'
                      : ''
                  }
                  hover:bg-gradient-to-r hover:from-gray-100/80 hover:to-gray-50/50 dark:hover:from-slate-700/60 dark:hover:to-slate-700/30
                  transition-all duration-150 ease-out
                  cursor-pointer
                  group
                  ${row.className || ''}
                `.trim()}
              >
                {row.cells.map((cell, cellIndex) => {
                  const isVisible = cell.isVisible !== undefined ? cell.isVisible : true;
                  if (!isVisible) return null;
                  const isFirstCell = cellIndex === 0;
                  return cell.type === CellTypeEnum.TH ? (
                    <th
                      key={`cell-${cellIndex}`}
                      scope="row"
                      className={`${cell.cellContentTemplate ? '' : 'py-4 px-4 whitespace-nowrap font-medium'} ${
                        isFirstCell ? 'text-gray-900 dark:text-white' : ''
                      } ${cell.className || ''}`.trim()}
                    >
                      {cell.cellContentTemplate ? cell.cellContentTemplate(cell, t, row.rowData) : `${cell.text}`}
                    </th>
                  ) : (
                    <td
                      key={`cell-${cellIndex}`}
                      className={`${cell.cellContentTemplate ? '' : 'py-4 px-4 whitespace-nowrap'} ${
                        cell.className || ''
                      }`.trim()}
                    >
                      {cell.cellContentTemplate ? cell.cellContentTemplate(cell, t, row.rowData) : cell.text}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {isLoading && (
        <div className="py-6 bg-gradient-to-b from-white to-gray-50/50 dark:from-slate-800 dark:to-slate-800/50">
          <TableSkeleton rows={3} columns={5} />
        </div>
      )}
    </div>
  );
};

export default Table;
